"use client";

import Lenis from "lenis";
import { m, useReducedMotion } from "motion/react";
import { useSearchParams } from "next/navigation";
import { Suspense, useEffect, useRef, type RefObject } from "react";

import { LandingFiveDimensionsSection } from "./_components/landing-five-dimensions-section";
import { LandingHero } from "./_components/landing-hero";
import { LandingNavBar } from "./_components/landing-nav-bar";
import { LandingPartnerLogos } from "./_components/landing-partner-logos";
import { LandingPrismHero } from "./_components/landing-prism-hero";
import { LandingResumePitch } from "./_components/landing-resume-pitch";
import { LandingStepsViewPager } from "./_components/landing-steps-view-pager";
import { RolloutsSection } from "./_components/rollouts-section";

/**
 * Landing: StandardDesign page root `8788:3598` — artboard 1512×4224, vertical stack (nav `9155:3692` 72px → sections).
 * Section refs: Prism hero `9155:3713`; partner logos `9223:3991`; five dimensions `8963:5003`; resume pitch `8788:6412`;
 * rollouts `8788:6432`; steps `8788:9949`; closing hero `8788:11381`. Nav variants: guest `9155:3692`, signed-in `8788:11432`.
 */
function ScrollToSectionFromQuery({
  lenisRef,
}: {
  lenisRef: RefObject<Lenis | null>;
}) {
  const searchParams = useSearchParams();

  useEffect(() => {
    const section = searchParams.get("section");
    const targetId =
      section === "matching"
        ? "landing-section-matching"
        : section === "why"
          ? "landing-section-why"
          : section === "how"
            ? "landing-section-how"
            : null;
    if (!targetId) return;

    requestAnimationFrame(() => {
      const target = document.getElementById(targetId);
      if (!target) return;

      if (lenisRef.current) {
        lenisRef.current.scrollTo(target, {
          duration: 1.15,
        });
        return;
      }

      target.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, [lenisRef, searchParams]);

  return null;
}

export default function LandingPage() {
  const lenisRef = useRef<Lenis | null>(null);
  const shouldReduceMotion = useReducedMotion();

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return;
    }

    const lenis = new Lenis({
      lerp: 0.09,
      duration: 1.1,
      smoothWheel: true,
      wheelMultiplier: 0.88,
      touchMultiplier: 1,
      syncTouch: false,
    });

    lenisRef.current = lenis;

    let rafId = 0;
    const onFrame = (time: number) => {
      lenis.raf(time);
      rafId = window.requestAnimationFrame(onFrame);
    };

    rafId = window.requestAnimationFrame(onFrame);

    return () => {
      window.cancelAnimationFrame(rafId);
      lenis.destroy();
      lenisRef.current = null;
    };
  }, []);

  return (
    <>
      <main className="mx-auto flex min-h-screen w-full flex-col overflow-x-clip bg-white">
        <Suspense fallback={null}>
          <ScrollToSectionFromQuery lenisRef={lenisRef} />
        </Suspense>
        <LandingNavBar />
        {[
          <LandingPrismHero key="prism-hero" />,
          <LandingPartnerLogos key="partner-logos" />,
          <LandingFiveDimensionsSection key="five-dimensions" />,
          <LandingResumePitch key="resume-pitch" />,
          <RolloutsSection key="rollouts" />,
          <div key="steps" className="w-full mx-auto max-w-[1512px]">
            <LandingStepsViewPager />
          </div>,
          <LandingHero key="landing-hero" />,
        ].map((section, index) => (
          <m.div
            key={section.key}
            custom={index}
            initial={shouldReduceMotion ? "visible" : "hidden"}
            whileInView="visible"
            viewport={{ amount: 0.2, once: true }}
            variants={{
              hidden: { opacity: 0, y: 42, filter: "blur(8px)" },
              visible: (customIndex: number) => ({
                opacity: 1,
                y: 0,
                filter: "blur(0px)",
                transition: {
                  duration: 0.75,
                  delay: customIndex * 0.08,
                  ease: [0.16, 1, 0.3, 1],
                },
              }),
            }}
          >
            {section}
          </m.div>
        ))}
      </main>
      <style jsx global>{`
        html.lenis,
        html.lenis body {
          height: auto;
        }

        .lenis.lenis-smooth {
          scroll-behavior: auto !important;
        }

        .lenis.lenis-stopped {
          overflow: hidden;
        }
      `}</style>
    </>
  );
}
